import { useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Layout } from '@/components/Layout';
import { translations, scrollToSection, type Language } from '@/lib/index';
import { IMAGES } from '@/assets/images';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Zap, DollarSign, AlertTriangle, Lightbulb, Hammer, Leaf, Wrench, MessageSquare, Sparkles, TrendingDown, Award, Cpu, Mail, Phone, MapPin } from 'lucide-react';

interface HomeProps {
  language: Language;
  setLanguage: (lang: Language) => void;
}

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 35 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};

const AnimatedSection = ({ children, id }: { children: React.ReactNode; id?: string }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <motion.section
      ref={ref}
      id={id}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      variants={fadeInUp}
    >
      {children}
    </motion.section>
  );
};

const AnimatedCard = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => {
  return (
    <motion.div
      variants={fadeInUp}
      transition={{ delay }}
      whileHover={{ scale: 1.02, transition: { type: 'spring', stiffness: 400, damping: 30 } }}
    >
      {children}
    </motion.div>
  );
};

export default function Home({ language, setLanguage }: HomeProps) {
  const t = translations[language];
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
  };

  return (
    <Layout language={language} setLanguage={setLanguage}>
      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.HERO_ATHLETE_2}
            alt="Sports facility"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 35, delay: 0.2 }}
            className="text-5xl md:text-7xl font-bold mb-6 tracking-tight"
          >
            {t.hero.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 35, delay: 0.4 }}
            className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-2xl mx-auto"
          >
            {t.hero.subtitle}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 35, delay: 0.6 }}
          >
            <Button
              size="lg"
              className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8 py-6 shadow-lg"
              onClick={() => scrollToSection('how-it-works')}
            >
              {t.hero.cta}
            </Button>
          </motion.div>
        </div>
      </section>

      <AnimatedSection id="about">
        <div className="container mx-auto px-4 py-24">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">{t.about.title}</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">{t.about.description}</p>
            </div>
            <div className="relative">
              <img
                src={IMAGES.SPORTS_FLOOR_2}
                alt="Smart sports floor parquet installation"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection id="problem">
        <div className="bg-muted/30 py-24">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">{t.problem.title}</h2>
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid md:grid-cols-3 gap-8"
            >
              <AnimatedCard>
                <Card className="h-full hover:shadow-xl transition-shadow">
                  <CardContent className="p-8">
                    <DollarSign className="w-12 h-12 text-destructive mb-4" />
                    <h3 className="text-2xl font-semibold mb-3">{t.problem.items.cost.title}</h3>
                    <p className="text-muted-foreground">{t.problem.items.cost.description}</p>
                  </CardContent>
                </Card>
              </AnimatedCard>
              <AnimatedCard delay={0.1}>
                <Card className="h-full hover:shadow-xl transition-shadow">
                  <CardContent className="p-8">
                    <AlertTriangle className="w-12 h-12 text-destructive mb-4" />
                    <h3 className="text-2xl font-semibold mb-3">{t.problem.items.inefficiency.title}</h3>
                    <p className="text-muted-foreground">{t.problem.items.inefficiency.description}</p>
                  </CardContent>
                </Card>
              </AnimatedCard>
              <AnimatedCard delay={0.2}>
                <Card className="h-full hover:shadow-xl transition-shadow">
                  <CardContent className="p-8">
                    <Lightbulb className="w-12 h-12 text-destructive mb-4" />
                    <h3 className="text-2xl font-semibold mb-3">{t.problem.items.innovation.title}</h3>
                    <p className="text-muted-foreground">{t.problem.items.innovation.description}</p>
                  </CardContent>
                </Card>
              </AnimatedCard>
            </motion.div>
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection id="solution">
        <div className="container mx-auto px-4 py-24">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src={IMAGES.SPORTS_FACILITY_1}
                alt="Smart flooring"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
            </div>
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">{t.solution.title}</h2>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">{t.solution.description}</p>
              <div className="bg-accent/10 border-l-4 border-accent p-6 rounded-lg">
                <p className="text-xl font-semibold text-accent">{t.solution.keyPhrase}</p>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection id="services">
        <div className="bg-muted/30 py-24">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">{t.services.title}</h2>
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
            >
              <AnimatedCard>
                <Card className="h-full hover:shadow-xl transition-all">
                  <CardContent className="p-8">
                    <Hammer className="w-12 h-12 text-accent mb-4" />
                    <h3 className="text-xl font-semibold mb-3">{t.services.items.parquet.title}</h3>
                    <p className="text-muted-foreground">{t.services.items.parquet.description}</p>
                  </CardContent>
                </Card>
              </AnimatedCard>
              <AnimatedCard delay={0.1}>
                <Card className="h-full hover:shadow-xl transition-all">
                  <CardContent className="p-8">
                    <Leaf className="w-12 h-12 text-accent mb-4" />
                    <h3 className="text-xl font-semibold mb-3">{t.services.items.turf.title}</h3>
                    <p className="text-muted-foreground">{t.services.items.turf.description}</p>
                  </CardContent>
                </Card>
              </AnimatedCard>
              <AnimatedCard delay={0.2}>
                <Card className="h-full hover:shadow-xl transition-all">
                  <CardContent className="p-8">
                    <Wrench className="w-12 h-12 text-accent mb-4" />
                    <h3 className="text-xl font-semibold mb-3">{t.services.items.maintenance.title}</h3>
                    <p className="text-muted-foreground">{t.services.items.maintenance.description}</p>
                  </CardContent>
                </Card>
              </AnimatedCard>
              <AnimatedCard delay={0.3}>
                <Card className="h-full hover:shadow-xl transition-all">
                  <CardContent className="p-8">
                    <MessageSquare className="w-12 h-12 text-accent mb-4" />
                    <h3 className="text-xl font-semibold mb-3">{t.services.items.consulting.title}</h3>
                    <p className="text-muted-foreground">{t.services.items.consulting.description}</p>
                  </CardContent>
                </Card>
              </AnimatedCard>
            </motion.div>
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection id="product">
        <div className="container mx-auto px-4 py-24">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">{t.product.title}</h2>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            <AnimatedCard>
              <Card className="h-full bg-accent/5 border-accent/20 hover:shadow-xl transition-all">
                <CardContent className="p-8 text-center">
                  <Zap className="w-16 h-16 text-accent mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-3">{t.product.features.energy.title}</h3>
                  <p className="text-muted-foreground">{t.product.features.energy.description}</p>
                </CardContent>
              </Card>
            </AnimatedCard>
            <AnimatedCard delay={0.1}>
              <Card className="h-full bg-accent/5 border-accent/20 hover:shadow-xl transition-all">
                <CardContent className="p-8 text-center">
                  <TrendingDown className="w-16 h-16 text-accent mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-3">{t.product.features.cost.title}</h3>
                  <p className="text-muted-foreground">{t.product.features.cost.description}</p>
                </CardContent>
              </Card>
            </AnimatedCard>
            <AnimatedCard delay={0.2}>
              <Card className="h-full bg-accent/5 border-accent/20 hover:shadow-xl transition-all">
                <CardContent className="p-8 text-center">
                  <Award className="w-16 h-16 text-accent mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-3">{t.product.features.terrain.title}</h3>
                  <p className="text-muted-foreground">{t.product.features.terrain.description}</p>
                </CardContent>
              </Card>
            </AnimatedCard>
            <AnimatedCard delay={0.3}>
              <Card className="h-full bg-accent/5 border-accent/20 hover:shadow-xl transition-all">
                <CardContent className="p-8 text-center">
                  <Cpu className="w-16 h-16 text-accent mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-3">{t.product.features.tech.title}</h3>
                  <p className="text-muted-foreground">{t.product.features.tech.description}</p>
                </CardContent>
              </Card>
            </AnimatedCard>
          </motion.div>
        </div>
      </AnimatedSection>

      <AnimatedSection id="sustainability">
        <div className="bg-muted/30 py-24">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">{t.sustainability.title}</h2>
            <p className="text-xl text-center text-muted-foreground mb-16">{t.sustainability.subtitle}</p>
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid md:grid-cols-2 gap-8"
            >
              <AnimatedCard>
                <Card className="h-full border-l-4 border-l-[#FCC30B] bg-[#FCC30B]/5 hover:shadow-xl transition-all">
                  <CardContent className="p-8">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-14 h-14 rounded-xl bg-[#FCC30B] flex items-center justify-center text-white text-2xl font-black shadow-md">7</div>
                      <h3 className="text-xl font-bold">{t.sustainability.sdgs.sdg7.name}</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">{t.sustainability.sdgs.sdg7.description}</p>
                  </CardContent>
                </Card>
              </AnimatedCard>
              <AnimatedCard delay={0.1}>
                <Card className="h-full border-l-4 border-l-[#FD6925] bg-[#FD6925]/5 hover:shadow-xl transition-all">
                  <CardContent className="p-8">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-14 h-14 rounded-xl bg-[#FD6925] flex items-center justify-center text-white text-2xl font-black shadow-md">9</div>
                      <h3 className="text-xl font-bold">{t.sustainability.sdgs.sdg9.name}</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">{t.sustainability.sdgs.sdg9.description}</p>
                  </CardContent>
                </Card>
              </AnimatedCard>
              <AnimatedCard delay={0.2}>
                <Card className="h-full border-l-4 border-l-[#FD9D24] bg-[#FD9D24]/5 hover:shadow-xl transition-all">
                  <CardContent className="p-8">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-14 h-14 rounded-xl bg-[#FD9D24] flex items-center justify-center text-white text-2xl font-black shadow-md">11</div>
                      <h3 className="text-xl font-bold">{t.sustainability.sdgs.sdg11.name}</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">{t.sustainability.sdgs.sdg11.description}</p>
                  </CardContent>
                </Card>
              </AnimatedCard>
              <AnimatedCard delay={0.3}>
                <Card className="h-full border-l-4 border-l-[#BF8B2E] bg-[#BF8B2E]/5 hover:shadow-xl transition-all">
                  <CardContent className="p-8">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-14 h-14 rounded-xl bg-[#BF8B2E] flex items-center justify-center text-white text-2xl font-black shadow-md">12</div>
                      <h3 className="text-xl font-bold">{t.sustainability.sdgs.sdg12.name}</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">{t.sustainability.sdgs.sdg12.description}</p>
                  </CardContent>
                </Card>
              </AnimatedCard>
            </motion.div>
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection id="how-it-works">
        <div className="container mx-auto px-4 py-24">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">{t.howItWorks.title}</h2>
          <div className="max-w-4xl mx-auto">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute left-8 top-16 bottom-16 w-0.5 bg-accent/30 hidden md:block" />
              <div className="space-y-12">
                <AnimatedCard>
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0 w-16 h-16 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-2xl font-bold shadow-lg">
                      1
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-semibold mb-3">{t.howItWorks.steps.step1.title}</h3>
                      <p className="text-muted-foreground text-lg">{t.howItWorks.steps.step1.description}</p>
                    </div>
                  </div>
                </AnimatedCard>
                <AnimatedCard delay={0.2}>
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0 w-16 h-16 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-2xl font-bold shadow-lg">
                      2
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-semibold mb-3">{t.howItWorks.steps.step2.title}</h3>
                      <p className="text-muted-foreground text-lg">{t.howItWorks.steps.step2.description}</p>
                    </div>
                  </div>
                </AnimatedCard>
                <AnimatedCard delay={0.4}>
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0 w-16 h-16 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-2xl font-bold shadow-lg">
                      3
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-semibold mb-3">{t.howItWorks.steps.step3.title}</h3>
                      <p className="text-muted-foreground text-lg">{t.howItWorks.steps.step3.description}</p>
                    </div>
                  </div>
                </AnimatedCard>
              </div>
            </motion.div>
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection id="gallery">
        <div className="bg-muted/30 py-24">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">{t.gallery.title}</h2>
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-2 md:grid-cols-3 gap-4"
            >
              {/* Row 1: large + 2 small */}
              <AnimatedCard>
                <img
                  src={IMAGES.HERO_ATHLETE_2}
                  alt="Indoor sports hall with smart flooring"
                  className="w-full h-72 object-cover rounded-xl shadow-lg hover:shadow-2xl hover:scale-[1.02] transition-all duration-300"
                />
              </AnimatedCard>
              <AnimatedCard delay={0.1}>
                <img
                  src={IMAGES.SPORTS_FLOOR_2}
                  alt="Professional parquet sports court"
                  className="w-full h-72 object-cover rounded-xl shadow-lg hover:shadow-2xl hover:scale-[1.02] transition-all duration-300"
                />
              </AnimatedCard>
              <AnimatedCard delay={0.15}>
                <img
                  src={IMAGES.SPORTS_FLOOR_3}
                  alt="Classic wooden gym floor"
                  className="w-full h-72 object-cover rounded-xl shadow-lg hover:shadow-2xl hover:scale-[1.02] transition-all duration-300"
                />
              </AnimatedCard>
              {/* Row 2 */}
              <AnimatedCard delay={0.2}>
                <img
                  src={IMAGES.SPORTS_FACILITY_8}
                  alt="Basketball game on smart floor"
                  className="w-full h-72 object-cover rounded-xl shadow-lg hover:shadow-2xl hover:scale-[1.02] transition-all duration-300"
                />
              </AnimatedCard>
              <AnimatedCard delay={0.25}>
                <img
                  src={IMAGES.ATHLETES_RUNNING_2}
                  alt="Badminton players on indoor sports court"
                  className="w-full h-72 object-cover rounded-xl shadow-lg hover:shadow-2xl hover:scale-[1.02] transition-all duration-300"
                />
              </AnimatedCard>
              <AnimatedCard delay={0.3}>
                <img
                  src={IMAGES.SPORTS_FLOOR_5}
                  alt="Sports ball on wooden floor detail"
                  className="w-full h-72 object-cover rounded-xl shadow-lg hover:shadow-2xl hover:scale-[1.02] transition-all duration-300"
                />
              </AnimatedCard>
              {/* Row 3: user-provided night facility photos */}
              <AnimatedCard delay={0.35}>
                <img
                  src={IMAGES.SPORTS_FACILITY_1}
                  alt="Sports facility at night lit court"
                  className="w-full h-72 object-cover rounded-xl shadow-lg hover:shadow-2xl hover:scale-[1.02] transition-all duration-300"
                />
              </AnimatedCard>
              <AnimatedCard delay={0.4}>
                <img
                  src={IMAGES.SPORTS_FLOOR_7}
                  alt="Basketball on sports floor close-up"
                  className="w-full h-72 object-cover rounded-xl shadow-lg hover:shadow-2xl hover:scale-[1.02] transition-all duration-300"
                />
              </AnimatedCard>
              <AnimatedCard delay={0.45}>
                <img
                  src={IMAGES.ATHLETES_RUNNING_1}
                  alt="Large indoor sports arena facility"
                  className="w-full h-72 object-cover rounded-xl shadow-lg hover:shadow-2xl hover:scale-[1.02] transition-all duration-300"
                />
              </AnimatedCard>
            </motion.div>
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection id="location">
        <div className="container mx-auto px-4 py-24">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">{t.location.title}</h2>
          <div className="max-w-2xl mx-auto">
            <Card className="overflow-hidden shadow-2xl">
              <CardContent className="p-0">
                <div className="bg-primary/5 p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <MapPin className="w-8 h-8 text-accent flex-shrink-0" />
                    <div>
                      <h3 className="text-2xl font-semibold mb-2">Sport Energy Floor</h3>
                      <p className="text-muted-foreground text-lg">{t.location.address}</p>
                      <p className="text-muted-foreground text-lg">{t.location.city}</p>
                    </div>
                  </div>
                </div>
                <div className="h-64 bg-muted">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3075.0234567890123!2d2.9123456789012345!3d39.71234567890123!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMznCsDQyJzQ0LjQiTiAywrA1NCc0NC40IkU!5e0!3m2!1sen!2ses!4v1234567890123!5m2!1sen!2ses"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection id="contact">
        <div className="bg-muted/30 py-24">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">{t.contact.title}</h2>
            <p className="text-xl text-center text-muted-foreground mb-16">{t.contact.cta}</p>
            <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-12">
              <div>
                <Card className="shadow-xl">
                  <CardContent className="p-8">
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium mb-2">{t.contact.form.name}</label>
                        <Input
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">{t.contact.form.email}</label>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">{t.contact.form.phone}</label>
                        <Input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">{t.contact.form.message}</label>
                        <Textarea
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          rows={4}
                          required
                        />
                      </div>
                      <Button type="submit" className="w-full bg-accent text-accent-foreground hover:bg-accent/90">
                        {t.contact.form.submit}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>
              <div className="space-y-6">
                <Card className="shadow-xl">
                  <CardContent className="p-8">
                    <div className="flex items-center gap-4 mb-6">
                      <Mail className="w-8 h-8 text-accent" />
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Email</p>
                        <a href={`mailto:${t.contact.info.email}`} className="text-lg font-medium hover:text-accent transition-colors">
                          {t.contact.info.email}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Phone className="w-8 h-8 text-accent" />
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">{t.contact.form.phone}</p>
                        <a href={`tel:${t.contact.info.phone}`} className="text-lg font-medium hover:text-accent transition-colors">
                          {t.contact.info.phone}
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>
    </Layout>
  );
}